import { useCallback, useEffect, useRef, useState } from 'react';
import Api from '../../Services/Api';
import { normalizeSavedProps } from '../propertyUtils';

export default function useManualPropertyConfig() {
    const [selectedType, setSelectedType] = useState('contact');
    const [allProperties, setAllProperties] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [loading, setLoading] = useState(false);

    const [dealTab, setDealTab] = useState(0);
    const [pipelines, setPipelines] = useState([]);
    const [selectedPipeline, setSelectedPipeline] = useState(null);
    const [selectedStage, setSelectedStage] = useState(null);
    const [stageProperties, setStageProperties] = useState([]);
    const [stageLoading, setStageLoading] = useState(false);

    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const savingRef = useRef(false);
    const successTimerRef = useRef(null);
    const errorTimerRef = useRef(null);

    const isStageMode = selectedType === 'deal' && dealTab === 1;
    const activeProps = isStageMode ? stageProperties : selectedProperties;
    const canSave = isStageMode ? !!(selectedPipeline && selectedStage) : true;
    const stageKey = selectedPipeline && selectedStage ? `${selectedPipeline.id}:${selectedStage.id}` : 'none';

    useEffect(() => () => {
        clearTimeout(successTimerRef.current);
        clearTimeout(errorTimerRef.current);
    }, []);

    const loadTypeData = useCallback((type) => {
        setSelectedType(type);
        setSelectedProperties([]);
        setDealTab(0);
        setSelectedPipeline(null);
        setSelectedStage(null);
        setStageProperties([]);
        setLoading(true);

        const promises = [
            Api.fetchProperties(type, true, true),
            Api.fetchDomainProperties(),
        ];
        if (type === 'deal') {
            promises.push(Api.fetchPipelines());
        }

        Promise.all(promises).then(([responseAll, domainResponse, pipelinesResult]) => {
            const allProps = Array.isArray(responseAll) ? responseAll : [];
            setAllProperties(allProps);
            setPipelines(Array.isArray(pipelinesResult) ? pipelinesResult : []);
            const domainProps = (domainResponse?.properties && typeof domainResponse.properties === 'object' && !Array.isArray(domainResponse.properties))
                ? domainResponse.properties
                : {};
            const savedTypeProps = Array.isArray(domainProps[type]) ? domainProps[type] : [];
            setSelectedProperties(normalizeSavedProps(savedTypeProps, allProps));
            setLoading(false);
        }).catch(() => {
            setAllProperties([]);
            setPipelines([]);
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        loadTypeData('contact');
    }, [loadTypeData]);

    const loadStageProperties = useCallback((pipeline, stage) => {
        if (!pipeline || !stage) return;
        setStageLoading(true);
        Api.fetchStageProperties(pipeline.id, stage.id).then((response) => {
            const saved = Array.isArray(response?.properties) ? response.properties : [];
            setStageProperties(saved.length > 0 ? normalizeSavedProps(saved, allProperties) : []);
            setStageLoading(false);
        }).catch(() => {
            setStageProperties([]);
            setStageLoading(false);
        });
    }, [allProperties]);

    const onSelectPipeline = (newPipeline) => {
        setSelectedPipeline(newPipeline);
        setStageProperties([]);
        if (newPipeline?.stages?.length > 0) {
            const firstStage = newPipeline.stages[0];
            setSelectedStage(firstStage);
            loadStageProperties(newPipeline, firstStage);
        } else {
            setSelectedStage(null);
        }
    };

    const onSelectStage = (newStage) => {
        setSelectedStage(newStage);
        setStageProperties([]);
        if (!newStage || !selectedPipeline) return;
        loadStageProperties(selectedPipeline, newStage);
    };

    const handleAddProperty = (_, prop) => {
        if (!prop) return;
        const current = isStageMode ? stageProperties : selectedProperties;
        if (current.some(p => p.name === prop.name)) return;
        const newProp = { name: prop.name, label: prop.label, mandatory: false, mandatoryIf: '', type: prop.type || '' };
        if (isStageMode) setStageProperties(prev => [...prev, newProp]);
        else setSelectedProperties(prev => [...prev, newProp]);
    };

    const removeProperty = (idx) => {
        if (isStageMode) setStageProperties(prev => prev.filter((_, i) => i !== idx));
        else setSelectedProperties(prev => prev.filter((_, i) => i !== idx));
    };

    const toggleMandatory = (idx) => {
        const updater = prev => prev.map((p, i) => i === idx ? { ...p, mandatory: !p.mandatory, mandatoryIf: !p.mandatory ? '' : p.mandatoryIf } : p);
        if (isStageMode) setStageProperties(updater);
        else setSelectedProperties(updater);
    };

    const changeMandatoryIf = (idx, value) => {
        const updater = prev => prev.map((p, i) => i === idx ? { ...p, mandatoryIf: value } : p);
        if (isStageMode) setStageProperties(updater);
        else setSelectedProperties(updater);
    };

    const handleSave = () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setSaveError(false);

        const onSuccess = () => {
            savingRef.current = false;
            setSaving(false);
            setSuccess(true);
            clearTimeout(successTimerRef.current);
            successTimerRef.current = setTimeout(() => setSuccess(false), 3000);
        };
        const onError = () => {
            savingRef.current = false;
            setSaving(false);
            setSaveError(true);
            clearTimeout(errorTimerRef.current);
            errorTimerRef.current = setTimeout(() => setSaveError(false), 5000);
        };

        if (isStageMode && selectedPipeline && selectedStage) {
            const propertiesToSave = stageProperties.map(({ name, mandatory, mandatoryIf, type }) => ({
                name, mandatory: !!mandatory, mandatoryIf: mandatoryIf || '', type: type || ''
            }));
            Api.updateStageProperties(selectedPipeline.id, selectedStage.id, propertiesToSave).then(onSuccess).catch(onError);
        } else {
            const propertiesToSave = selectedProperties.map(({ name, mandatory, mandatoryIf, type }) => ({
                name, mandatory: !!mandatory, mandatoryIf: mandatoryIf || '', type: type || ''
            }));
            Api.updateProperties(selectedType, { properties: propertiesToSave }).then(onSuccess).catch(onError);
        }
    };

    return {
        selectedType,
        loading,
        dealTab,
        pipelines,
        selectedPipeline,
        selectedStage,
        stageLoading,
        isStageMode,
        activeProps,
        canSave,
        stageKey,
        success,
        saveError,
        saving,
        allProperties,
        loadTypeData,
        setDealTab,
        setSelectedPipeline,
        setSelectedStage,
        loadStageProperties,
        onSelectPipeline,
        onSelectStage,
        handleAddProperty,
        removeProperty,
        toggleMandatory,
        changeMandatoryIf,
        handleSave,
    };
}

